import type { CallView, PhoneErrorStage, StartCallBody } from "@gioia365/contracts";
import { ApiRequestError } from "../api/http";
import { sameAudioChoice, type AudioChoice } from "./audio-devices";
import {
  browserPhoneErrorOf,
  phoneErrorDetailOf,
  type BrowserCall,
  type BrowserPhone,
  type BrowserPhoneError,
  type PhoneErrorDetail,
} from "./browser-phone";
import { IDLE, isCallLive, newRequestId, type CallSession, type CallTarget, type OutcomeOrigin } from "./call-session-state";
import type { PhoneErrorReport } from "./phone-error-report";
import { requestCallHangup, requestCallStart } from "./telephony-queries";

// La chiamata che parte e il telefono del browser (piano M4, Task 13, D4 e D10), per `call-session.tsx`: il server crea la
// chiamata dal posto del telefono, poi il token, poi il telefono compone con il solo `callId`; gli eventi del telefono
// portano la sessione da `starting` a `ringing`, `live` e `outcome`. Una chiamata che non parte (rifiuto, microfono, SDK,
// errore del fornitore prima della risposta) si abbandona sul server e la sessione dice perché (`error`). Il telefono compone
// con il microfono e l'ascolto scelti su questo computer (richiesta del product owner del 6 ottobre 2026). Ogni errore del
// telefono, oltre al messaggio di sempre, va al server come resoconto (TEL-33, `phone-error-report.ts`), una volta sola per
// errore e al più `ATTEMPT_REPORT_LIMIT` per chiamata: il resoconto non cambia mai la chiamata né la sessione.

/**
 * Il tentativo di chiamata in corso: gli eventi di uno più vecchio non cambiano più la sessione. `answeredAt`: quando il Lead
 * ha risposto, con l'orologio del browser (`null` finché non risponde), per la durata della conversazione. `stage`: la fase
 * della chiamata, soltanto per il resoconto di un errore (TEL-33): va solo avanti, e «after» vuol dire dopo il riaggancio di chi
 * chiama.
 */
export type Attempt = { browserCall: BrowserCall | null; hungUp: boolean; answeredAt: number | null; stage: PhoneErrorStage };

/** Quanti resoconti diversi al massimo manda una chiamata (TEL-33): il `Device` e la chiamata dicono spesso lo stesso errore. */
export const ATTEMPT_REPORT_LIMIT = 3;

/** Ciò che serve alla chiamata dalla sessione: lo stato di adesso, il tentativo, il token, l'abbandono. */
export type DialContext = {
  phone: BrowserPhone;
  read(): CallSession;
  set(next: CallSession): void;
  attempt: { current: Attempt | null };
  isMounted(): boolean;
  token(target: CallTarget): Promise<string>;
  /** Un token rifiutato dal fornitore non si riusa. */
  forgetToken(target: CallTarget): void;
  /** `true` se il server ha chiuso la chiamata mai partita, `false` se il fornitore la conosce già. */
  abandon(target: CallTarget, call: CallView): Promise<boolean>;
  toOutcome(from: OutcomeOrigin, call: CallView, notice: BrowserPhoneError | null): void;
  refresh(target: CallTarget): void;
  /** Il microfono e l'ascolto scelti adesso su questo computer (`audio-devices.ts`). */
  audio(): AudioChoice;
  /** Il resoconto di un errore del telefono al server (TEL-33): in sottofondo, non lancia e non si aspetta. */
  report(target: CallTarget, report: PhoneErrorReport): void;
};

const apiErrorOf = (error: unknown) => (error instanceof ApiRequestError ? error : new ApiRequestError(0, "unexpected_response"));

/** Quanto al massimo una chiamata nuova aspetta l'abbandono di quella di prima: oltre, parte comunque. */
export const PREVIOUS_ATTEMPT_WAIT_MS = 10_000;

/** Aspetta che la chiamata di prima abbia finito di nascere o di abbandonarsi sul server, al massimo `PREVIOUS_ATTEMPT_WAIT_MS`. */
function settledOrLater(previous: Promise<void>): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, PREVIOUS_ATTEMPT_WAIT_MS);
    void previous.finally(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}

/**
 * Fa nascere la chiamata e compone. `previous` è la chiamata di prima ancora in corso sul server (chiusa prima che il telefono
 * componesse, quindi da abbandonare quando arriva la risposta che aspettava): la nuova la aspetta, già in `starting`, prima di
 * chiedere la sua al server, così il server non trova il Lead ancora in chiamata (`lead_in_call`, minore di R2 per R3); chiusa
 * anche questa nell'attesa, non nasce nulla.
 */
export async function dial(context: DialContext, target: CallTarget, leadName: string, previous: Promise<void> | null = null): Promise<void> {
  const { phone, read, set } = context;
  if (target.phone === null) return;
  const attempt: Attempt = { browserCall: null, hungUp: false, answeredAt: null, stage: "before_dial" };
  context.attempt.current = attempt;
  const mine = () => context.attempt.current === attempt && context.isMounted();
  set({ phase: "starting", target, leadName, call: null });
  if (previous !== null) {
    await settledOrLater(previous);
    if (!mine()) return;
  }

  // Il resoconto al server di un errore del telefono (TEL-33), prima di qualunque scelta sulla sessione: lo stesso errore
  // (il `Device` e la chiamata lo dicono entrambi) parte una volta sola, e al più `ATTEMPT_REPORT_LIMIT` per chiamata. Non
  // cambia nulla della chiamata, e un suo guasto resta qui.
  const reported = new Set<string>();
  const report = (error: BrowserPhoneError, detail: PhoneErrorDetail, callId: string | null) => {
    try {
      const code = detail.code ?? (error.kind === "twilio" ? error.code : null);
      const key = [error.kind, code, detail.name, detail.message].join("|");
      if (reported.has(key) || reported.size >= ATTEMPT_REPORT_LIMIT) return;
      reported.add(key);
      context.report(target, { kind: error.kind, source: detail.source, stage: attempt.stage, code, name: detail.name, message: detail.message, callId });
    } catch {
      // Il resoconto non è mai un errore di chi chiama.
    }
  };

  const fail = async (call: CallView | null, error: BrowserPhoneError | ApiRequestError) => {
    if (!(error instanceof ApiRequestError)) context.forgetToken(target);
    if (call !== null) await context.abandon(target, call);
    if (!mine()) return;
    context.attempt.current = null;
    set({ phase: "error", target, leadName, call, error });
  };
  // «Chiudi la chiamata» prima che il telefono componesse (`hangUpCall` ha già lasciato il tentativo), o il guscio chiuso:
  // la chiamata non parte, e si abbandona qui, una volta sola.
  const stopped = async (call: CallView) => {
    if (mine()) return false;
    await context.abandon(target, call);
    return true;
  };

  const body: StartCallBody = {
    phone: target.phone,
    requestId: newRequestId(),
    ...(target.dialerSessionId === null ? {} : { dialerSessionId: target.dialerSessionId }),
  };
  let call: CallView;
  let provider;
  try {
    ({ call, provider } = await requestCallStart(target.tab, target.leadId, body));
  } catch (error) {
    // Il motivo può essere cambiato (un collega al telefono): la scheda si rilegge.
    context.refresh(target);
    await fail(null, apiErrorOf(error));
    return;
  }
  if (await stopped(call)) return;
  set({ phase: "starting", target, leadName, call });

  let token: string;
  try {
    token = await context.token(target);
  } catch (error) {
    await fail(call, apiErrorOf(error));
    return;
  }
  if (await stopped(call)) return;

  const created = call;
  const ongoing = () => {
    const now = read();
    return mine() && isCallLive(now) ? now : null;
  };
  /** Un errore del telefono prima della risposta: la chiamata non è partita, se il fornitore non la conosce. */
  const failedBeforeAnswer = (error: BrowserPhoneError) => {
    void context.abandon(target, created).then((closed) => {
      if (context.attempt.current !== attempt) return;
      if (closed) {
        context.attempt.current = null;
        set({ phase: "error", target, leadName, call: created, error });
        return;
      }
      // Il fornitore la conosce: la chiudono i suoi stati, e si chiede l'esito.
      const latest = read();
      if (latest.phase === "outcome") set({ ...latest, notice: error });
      else if (isCallLive(latest)) context.toOutcome(latest, created, error);
    });
  };
  const audio = context.audio();
  try {
    const browserCall = await phone.call({
      provider,
      token,
      params: { callId: created.id },
      audio,
      events: {
        onRinging: () => {
          if (attempt.stage === "before_dial") attempt.stage = "ringing";
          const now = ongoing();
          if (now?.phase === "starting") set({ phase: "ringing", target, leadName, call: created, startedAt: Date.now() });
        },
        onAccepted: () => {
          if (attempt.stage !== "after") attempt.stage = "conversation";
          attempt.answeredAt ??= Date.now();
          const now = ongoing();
          if (now) set({ phase: "live", target, leadName, call: created, startedAt: attempt.answeredAt });
        },
        // Chi risponde chiude, oppure la chiamata finisce da sé: si chiede l'esito.
        // La fase resta quella di prima: il SDK dice certi errori (il microfono negato) dopo aver chiuso, e contano per la
        // fase in cui la chiamata si è fermata. «after» è soltanto dopo il riaggancio di chi chiama (`hangUpCall`).
        onClosed: () => {
          const now = ongoing();
          if (now && !attempt.hungUp) context.toOutcome(now, created, null);
        },
        onError: (error, detail) => {
          report(error, detail ?? { source: "call", code: null, name: null, message: null }, created.id);
          if (context.attempt.current !== attempt || attempt.hungUp) return;
          attempt.browserCall = null;
          context.forgetToken(target);
          if (attempt.answeredAt === null) {
            // Anche dopo la chiusura: il SDK dice il microfono negato dopo aver chiuso la chiamata.
            failedBeforeAnswer(error);
            return;
          }
          // La conversazione c'è stata: si chiede l'esito, con l'errore detto.
          const now = read();
          if (now.phase === "outcome") set({ ...now, notice: error });
          else if (isCallLive(now)) context.toOutcome(now, created, error);
        },
      },
    });
    attempt.browserCall = browserCall;
    if (attempt.stage === "before_dial") attempt.stage = "ringing";
    if (!mine()) {
      // Chiusa mentre il telefono componeva: la si chiude appena c'è, e si abbandona (il fornitore, se la conosce già, la
      // chiude con i suoi stati).
      browserCall.hangUp();
      await context.abandon(target, created);
      return;
    }
    // Il microfono cambiato mentre il telefono componeva: la chiamata prende quello di adesso.
    const now = context.audio();
    if (!sameAudioChoice(now, audio) && isCallLive(read())) browserCall.switchAudio(now);
  } catch (error) {
    const refused = browserPhoneErrorOf(error);
    report(refused, phoneErrorDetailOf(error, refused.kind === "load" ? "sdk_load" : "connect"), created.id);
    await fail(created, refused);
  }
}

/**
 * «Chiudi la chiamata» durante la chiamata: riaggancia e chiede l'esito. Prima che il telefono abbia composto (`starting`:
 * il server, il token o il telefono non hanno ancora risposto) la postazione torna subito pronta e il tentativo si lascia:
 * `dial` abbandona la chiamata del server, una volta sola, appena la risposta che aspettava arriva.
 */
export async function hangUpCall(context: DialContext): Promise<void> {
  const now = context.read();
  const attempt = context.attempt.current;
  if (!attempt || !isCallLive(now)) return;
  attempt.hungUp = true;
  attempt.stage = "after";
  const browserCall = attempt.browserCall;
  if (now.call !== null && browserCall !== null) {
    const closing = { phase: "ending" as const, target: now.target, leadName: now.leadName, call: now.call };
    context.set(closing);
    try {
      await requestCallHangup(now.target.tab, now.call.id, AbortSignal.timeout(2_000));
    } catch {
      // Riaggancia anche senza rete; una cancellazione non misurabile non fa avanzare la sequenza.
    } finally {
      browserCall.hangUp();
    }
    if (context.attempt.current === attempt && context.isMounted()) context.toOutcome(closing, closing.call, null);
    return;
  }
  attempt.browserCall?.hangUp();
  context.attempt.current = null;
  context.set(IDLE);
}
